import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import ModalLoadingOverlay from "./ModalLoadingOverlay";
import { getApiErrorMessage } from "../config/toastHelpers";
import { userService } from "../services/services";
import { authStorage } from "../lib/authStorage";
import {
  getUserDisplayName,
  getUserPhone,
  getUserPincode,
  getUserAddress,
  getUserCity,
  getUserState,
} from "../lib/userDetails";
import type { ExchangeDraftPayload } from "../features/trade/types";
import { ModalFrame } from "../features/trade/components/ModalFrame";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { UserSummaryCard } from "../features/trade/components/UserSummaryCard";
import { WarehouseAddressCard } from "../features/trade/components/WarehouseAddressCard";

type ExchangeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  exchangeDraft: ExchangeDraftPayload | null;
  onBack?: () => void;
};

export default function ExchangeModal({
  isOpen,
  onClose,
  exchangeDraft,
  onBack,
}: ExchangeModalProps) {
  const t = useTranslations("modals");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const { userDetails, loading: loadingUser } = useCurrentUser();

  useEffect(() => {
    if (!isOpen) return;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSubmitting(false);
      setSubmitError("");
      return;
    }

    setSubmitting(false);
    setSubmitError("");
  }, [isOpen]);

  if (!isOpen) return null;

  const senderName = exchangeDraft?.fullName || getUserDisplayName(userDetails) || "";

  const handleSampleRequest = async () => {
    setSubmitError("");

    if (!exchangeDraft) {
      setSubmitError(
        "Missing exchange details. Please reopen the exchange form and try again.",
      );
      return;
    }

    if (!authStorage.hasSession()) {
      setSubmitError("You are not logged in. Please login and try again.");
      return;
    }

    setSubmitting(true);
    try {
      await userService.createSample({
        productId: exchangeDraft.productToGiveId,
        exchangeProductId: exchangeDraft.productToReceiveId,
        operation: exchangeDraft.operation,
        recipientName: "Agarmart",
        streetAddress: getUserAddress(userDetails),
        apartment: userDetails?.apartment || undefined,
        city: getUserCity(userDetails),
        state: getUserState(userDetails),
        pincode: getUserPincode(userDetails),
        phoneNumber: getUserPhone(userDetails),
        senderName,
        fullName: senderName,
      });
      onClose();
    } catch (error: unknown) {
      setSubmitError(
        getApiErrorMessage(
          error,
          "Failed to request sample. Please try again.",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalFrame
      panelClassName="
relative
flex
w-full
max-w-md
max-h-[92dvh]
flex-col
overflow-hidden
rounded-xl
bg-white
shadow-xl
"
    >
      {(submitting || loadingUser) && (
        <ModalLoadingOverlay
          message={
            loadingUser ? t("loadingDetails") : t("sell.submittingSample")
          }
        />
      )}

      <div className="sticky top-0 z-20 shrink-0 bg-white">
        <button
          onClick={() => {
            if (!submitting && !loadingUser) onClose();
          }}
          disabled={submitting || loadingUser}
          aria-label="Close"
          className="absolute right-4 top-4 z-10 rounded-full p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="h-5 w-5"
          >
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        <div className="flex flex-col">
          <div className="px-4 py-5 text-center">
            <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
              {t("exchange.sampleTitle")}
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              {t("exchange.sampleDesc")}
            </p>
          </div>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-6 sm:px-6">
        <div className="space-y-6">
          {userDetails && (
            <UserSummaryCard
              title={`${t("yourDetails")}`}
              items={[
                {
                  label: t("labels.name"),
                  value: exchangeDraft?.fullName || getUserDisplayName(userDetails) || "-",
                },
                {
                  label: t("labels.address"),
                  value: getUserAddress(userDetails) || "-",
                },
                {
                  label: t("labels.city"),
                  value: getUserCity(userDetails) || "-",
                },
                {
                  label: t("labels.state"),
                  value: getUserState(userDetails) || "-",
                },
                {
                  label: t("labels.phone"),
                  value: getUserPhone(userDetails) || "-",
                },
                {
                  label: t("labels.pinCode"),
                  value: getUserPincode(userDetails) || "-",
                },
              ]}
            />
          )}

          <p className="text-center text-gray-600">
            {t("exchange.senderText")}
          </p>

          <WarehouseAddressCard
            title={t("warehouseAddress")}
            name="AgarMart HQ"
            addressLines={[
              "10-ts lohamandi, Agarsen Square,",
              "Indore, Madhya Pradesh",
            ]}
            mapHref="https://maps.google.com/?q=10-ts+lohamandi+Agarsen+Square+Indore+Madhya+Pradesh"
          />

          <div className="mt-4 flex gap-2 rounded-xl border border-yellow-200 bg-yellow-50 p-4">
            <p className="text-sm text-yellow-800">
              {t("exchange.dispatchWarning")}
            </p>
          </div>

          <div className="space-y-3 pt-2">
            {submitError && (
              <p className="mt-1 text-xs text-red-500">{submitError}</p>
            )}

            <button
              onClick={handleSampleRequest}
              disabled={submitting || loadingUser}
              className="w-full rounded-lg bg-blue-500 py-3 font-bold text-white shadow-lg hover:bg-blue-600 disabled:opacity-60"
            >
              {submitting ? t("submitting") : t("sendSample")}
            </button>

            <button
              onClick={() => {
                if (!submitting && !loadingUser) {
                  setSubmitError("");
                  if (onBack) {
                    onBack();
                  } else {
                    onClose();
                  }
                }
              }}
              disabled={submitting || loadingUser}
              className="w-full text-xs font-bold uppercase text-gray-400 hover:text-gray-600"
            >
              {t("back")}
            </button>
          </div>
        </div>
      </div>
    </ModalFrame>
  );
}
